<template>
    <div class="pagaende-operation">
        <div class="header-section">
            <div class="header">
                <h2>Pågående operation</h2>
                <h3>{{ getViewTitle }}</h3>
            </div>
            <div class="header-section-left">
                <div class="toolbar">
                    <div v-if="!anonymousData" class="show-person-id">
                        <div class="labeled-checkbox">
                            <FormulateInput type="checkbox" id="person-id-checkbox" v-model="showPersonID" />
                            <label for="person-id-checkbox" class="checkbox-label">Visa PersonID</label>
                        </div>
                    </div>
                    <button @click="today" class="today-button" :class="{ 'active-tab': isToday }">Idag</button>
                    <FormulateInput
                        class="datepicker"
                        :clear-on-selection="true"
                        placeholder="Sök datum"
                        name="picked-time"
                        type="datepicker"
                        @input="goToDate"
                        @blur="goToDate"
                    />
                    <div class="arrow-buttons">
                        <button class="arrow-button" @click="previous">
                            <img src="@/assets/arrow_left_white.svg" alt="Föregående" />
                        </button>
                        <button class="arrow-button" @click="next">
                            <img src="@/assets/arrow_right_white.svg" alt="Nästa" />
                        </button>
                    </div>
                </div>
                <div class="legend">
                    <div class="legend-entry">
                        <img src="@/assets/pagaop_on_time.svg" />
                        <div class="color-box in-time"></div>
                        <span class="legend-text">I tid</span>
                    </div>
                    <div class="legend-entry">
                        <img src="@/assets/pagaop_late.svg" />
                        <div class="color-box time-exceeded"></div>
                        <span class="legend-text">Gått över tid</span>
                    </div>
                    <div class="legend-entry">
                        <div class="color-box x-min-left"></div>
                        <span class="legend-text">{{ operationXMinuterKvar }} minuter kvar</span>
                    </div>
                    <div class="legend-entry">
                        <img src="@/assets/pagaop_done.svg" />
                        <span class="legend-text">Genomförd</span>
                    </div>
                    <div class="legend-entry">
                        <img src="@/assets/patient_ankommit.svg" />
                        <span class="legend-text">Ankommit preop</span>
                    </div>
                    <div class="legend-entry">
                        <img src="@/assets/patient_gatt.svg" />
                        <span class="legend-text">Utskriven postop</span>
                    </div>
                </div>
            </div>
        </div>
        <FullCalendar :options="calendarOptions" ref="fullCalendar">
            <template #resourceLabelContent="arg">
                <div class="resource-header">
                    <div class="resource-title">{{ arg.resource.title }}</div>
                    <KButton
                        v-if="can({ action: 'update', subject: 'salsbemanning' })"
                        @click="openPersonalOchTider(arg.resource.extendedProps.salsbemanning)"
                        variant="none"
                        size="icon"
                    >
                        <img :src="IconEditPen" />
                    </KButton>
                </div>
                <div class="salsbemanning">
                    <div
                        v-for="personal in arg.resource.extendedProps.salsbemanning.personal"
                        :key="personal.id"
                        class="box personal"
                        :class="{
                            kirurg: personal.profile_display.roll_display.code === 'kirurg',
                        }"
                    >
                        <img :src="getBemanningIconSrc(personal)" class="personal-icon" />
                        <div>
                            <div class="personal-name">
                                {{ personal.profile_display.user_display.name_short }}
                            </div>
                            <div class="personal-time">
                                {{ getTime(personal.fran) }} -
                                {{ getTime(personal.till) }}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="arg.resource.extendedProps.salsbemanning.kommentar"
                    v-tooltip="{
                        content: arg.resource.extendedProps.salsbemanning.kommentar,
                        theme: 'kliniken-tooltip',
                    }"
                    class="box salsbemanning-kommentar"
                >
                    {{ arg.resource.extendedProps.salsbemanning.kommentar }}
                </div>
            </template>
            <template #eventContent="arg">
                <template v-if="arg.event.display === 'background'">
                    <div class="background-event">
                        <span class="title">
                            {{ arg.event.title }}
                        </span>
                    </div>
                </template>
                <VMenu v-else :triggers="[]" :shown="arg.event.extendedProps.showPopup" placement="auto">
                    <div class="event-info">
                        <img v-if="arg.event.extendedProps.aktivOpdel > 0" :src="getOpIconSrc(arg.event)" />
                        <span>{{ arg.event.extendedProps.huvudoperator.user_display.username ?? "" }}</span>
                        <span>{{ arg.event.extendedProps.opkod.code }}</span>
                        <span v-if="showPersonID" class="person-id">
                            {{ arg.event.extendedProps.patient?.personId_formatted }}
                        </span>
                    </div>
                    <div class="op-body-wrapper">
                        <div
                            class="opdel forberedelser"
                            :style="{
                                backgroundColor: opdelITid(arg.event, 10),
                                minWidth:
                                    (arg.event.extendedProps.forberedelser / arg.event.extendedProps.salstid) * 100 +
                                    '%',
                            }"
                        ></div>
                        <div
                            class="opdel optid"
                            :style="{
                                backgroundColor: opdelITid(arg.event, 20),
                                minWidth: (arg.event.extendedProps.optid / arg.event.extendedProps.salstid) * 100 + '%',
                            }"
                        ></div>
                        <div
                            class="opdel avveckling"
                            :style="{
                                backgroundColor: opdelITid(arg.event, 30),
                                minWidth:
                                    (arg.event.extendedProps.avveckling / arg.event.extendedProps.salstid) * 100 + '%',
                            }"
                        ></div>
                        <div
                            v-if="arg.event.extendedProps.xMinuterKvar"
                            class="x-minuter-kvar"
                            :style="{
                                left:
                                    (minutesUntilXMinuterKvar(arg.event) / arg.event.extendedProps.salstid) * 100 + '%',
                                minWidth: (operationXMinuterKvar / arg.event.extendedProps.salstid) * 100 + '%',
                            }"
                        ></div>
                    </div>
                    <template #popper>
                        <PagaendeOperationDetaljinfo
                            :event="arg.event"
                            :showButtons="!anonymousData"
                            :canReadOperationsjournal="canReadOperationsjournal"
                            :showPersonID="showPersonID"
                        />
                    </template>
                </VMenu>
            </template>
        </FullCalendar>
        <PersonalOchTider
            v-if="showPersonalOchTider"
            @submitted="personalOchTiderSubmitted"
            @close="closePersonalOchTider"
            :salsbemanning="salsbemanning"
            :tabId="tabId"
        />
    </div>
</template>
<script>
    import BaseTab from "@/tabs/mixins/BaseTab";
    import tabIcon from "@/assets/bed_icon.svg";
    import KButton from "@/components/Button.vue";
    import FullCalendar from "@fullcalendar/vue";
    import interactionPlugin from "@fullcalendar/interaction";
    import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
    import svLocale from "@fullcalendar/core/locales/sv";
    import { klinikenApi, getConfig } from "@/api";
    import moment from "moment";
    import { mapGetters } from "vuex";
    import emitter from "tiny-emitter/instance";
    import { extractHourMinuteFromDateTime } from "@/tabs/Operation/utils.js";
    import IconPagaopDone from "@/assets/pagaop_done.svg";
    import IconPagaopOnTime from "@/assets/pagaop_on_time.svg";
    import IconPagaopLate from "@/assets/pagaop_late.svg";
    import IconPatientAnkommit from "@/assets/patient_ankommit.svg";
    import IconPatientGatt from "@/assets/patient_gatt.svg";
    import IconKirurg from "@/assets/bemanning_kirurg.svg";
    import IconAnestesi from "@/assets/bemanning_anestesi.svg";
    import IconPersonal from "@/assets/bemanning_personal.svg";
    import IconEditPen from "@/assets/edit.svg";
    import PagaendeOperationDetaljinfo from "@/tabs/Operation/PagaendeOperationDetaljinfo.vue";
    import PersonalOchTider from "@/tabs/Operation/PersonalOchTider.vue";
    import { capitalizeFirstLetter } from "@/tabs/Operation/calendar-utils";

    const LIGHT_GREY = "#DEE2E3";
    const LIGHT_RED = "#FFA4A9";
    const LIGHT_GREEN = "#9BE2C3";
    const DARK_RED = "#E96669";
    const DARK_GREEN = "#34AC8D";

    /* Enum för aktiv opdel, matchas i backend i pagaendeoperation.AktivOpdel */
    const AKTIV_OPDEL = Object.freeze({
        PATIENT_EJ_ANKOMMIT: 0,
        PATIENT_ANKOMST: 1,
        FORBEREDELSER: 10,
        OPTID: 20,
        AVVECKLING: 30,
        AVVECKLING_AVSLUTAD: 40,
        PATIENT_UT: 50,
    });

    export default {
        extends: BaseTab,
        name: "PagaendeOperation",
        tabName: "Pågående operation",
        icon: tabIcon,
        components: {
            FullCalendar,
            KButton,
            PagaendeOperationDetaljinfo,
            PersonalOchTider,
        },

        data() {
            return {
                calendarApi: null,
                reloadTimeout: null,
                calendarOptions: {
                    plugins: [interactionPlugin, resourceTimelinePlugin],
                    schedulerLicenseKey: import.meta.env.VITE_FULLCALENDAR_SCHEDULER_LICENSE_KEY || "",
                    initialView: "resourceTimeline",
                    headerToolbar: false,
                    resourceOrder: "ordning,namn",
                    resources: [],
                    locale: svLocale,
                    events: [],
                    weekends: true,
                    slotMinTime: "07:00:00",
                    slotMaxTime: "19:00:00",
                    resourceLabelClassNames: "resource-label-cell",
                    resourceAreaHeaderContent: "Opsal",
                    nowIndicator: true,
                    eventMinWidth: 1,
                    eventColor: "transparent",
                    resourceAreaWidth: "18.125rem",
                    eventClick: this.eventClick,
                    views: {
                        resourceTimeline: {
                            titleFormat: {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            },
                        },
                    },
                },
                showPersonID: false,
                reloadIdleTime: 1000 * 60, // 1 minut
                canReadOperationsjournal: false,
                getTime: extractHourMinuteFromDateTime,
                salsbemanningList: [],
                handelser: [],
                showPersonalOchTider: false,
                salsbemanning: null,
            };
        },

        computed: {
            ...mapGetters("systemData", ["operationXMinuterKvar"]),
            ...mapGetters("ability", ["can"]),
            ...mapGetters("loginMode", ["anonymousData", "pathname"]),
            ...mapGetters("idle", ["lastActive"]),

            IconEditPen() {
                return IconEditPen;
            },

            apiUrl() {
                return this.anonymousData ? "/operation/pagaendeoperation/anonymous/" : "/operation/pagaendeoperation/";
            },

            hasCalenderApi() {
                return this.calendarApi && this.calendarApi.currentData;
            },
            getViewTitle() {
                return this.hasCalenderApi ? capitalizeFirstLetter(this.calendarApi.currentData.viewTitle) : "";
            },
            currentDate() {
                return this.hasCalenderApi
                    ? moment(this.calendarApi.currentData.dateProfile.currentDate).format("YYYY-MM-DD")
                    : null;
            },
            isToday() {
                const today = new Date();
                return this.hasCalenderApi
                    ? this.calendarApi.currentData.currentDate?.getDate() === today.getDate()
                    : false;
            },
        },

        watch: {
            // Koll för att avgöra om användaren varit inaktiv för länge och timeouten + operationer behöver "refreshas"
            lastActive: async function lastActive(newActive, oldActive) {
                if (newActive.getTime() - oldActive.getTime() >= this.reloadIdleTime) {
                    await this.updateView();
                    this.setReloadTimeout();
                }
            },
        },

        methods: {
            getBemanningIconSrc(personal) {
                switch (personal.profile_display.roll_display.code) {
                    case "kirurg":
                        return IconKirurg;
                    case "anestesilakare":
                        return IconAnestesi;
                    case "anestesisjukskoterska":
                        return IconAnestesi;
                    default:
                        return IconPersonal;
                }
            },
            async goToDate(date) {
                if (!date) {
                    return;
                }
                this.calendarApi.gotoDate(date);
                await this.updateView();
            },
            async today() {
                this.calendarApi.today();
                await this.updateView();
            },
            async previous() {
                this.calendarApi.prev();
                await this.updateView();
            },
            async next() {
                this.calendarApi.next();
                await this.updateView();
            },

            /**
             * Ikonen avgörs av om den senast aktiva opdelen är i tid eller inte, eller om operationen har status Genomförd.
             * Aktiv opdel:
             * 0 - Förberedelser ej startade
             * 1 - Patient ankommit
             * 10 - Förberedelser
             * 20 - Optid
             * 30 - Avveckling
             * 40 - Avveckling avslutad
             * 50 - Patient ut från postop
             */
            getOpIconSrc(event) {
                if (event.extendedProps.aktivOpdel === AKTIV_OPDEL.PATIENT_UT) {
                    return IconPatientGatt;
                }

                if (event.extendedProps.status === "Genomförd") {
                    return IconPagaopDone;
                }

                switch (event.extendedProps.aktivOpdel) {
                    case AKTIV_OPDEL.PATIENT_EJ_ANKOMMIT:
                        return "";
                    case AKTIV_OPDEL.PATIENT_ANKOMST:
                        return IconPatientAnkommit;
                    case AKTIV_OPDEL.FORBEREDELSER:
                        if (event.extendedProps.forberedelserITid) {
                            return IconPagaopOnTime;
                        }
                        break;
                    case AKTIV_OPDEL.OPTID:
                        if (event.extendedProps.optidITid) {
                            return IconPagaopOnTime;
                        }
                        break;
                    case AKTIV_OPDEL.AVVECKLING:
                        if (event.extendedProps.avvecklingITid) {
                            return IconPagaopOnTime;
                        }
                        break;
                    case AKTIV_OPDEL.AVVECKLING_AVSLUTAD:
                        if (event.extendedProps.avvecklingITid) {
                            return IconPagaopOnTime;
                        }
                        break;
                }
                return IconPagaopLate;
            },

            /**
             * @param event Om event.extendedProps.aktivOpdel är mellan 1-3 är operationen pågående och färgerna ska vara starkare.
             * @param opdel 1 - Förberedelser, 2 - Optid, 3 - Avveckling
             */
            opdelITid(event, opdel) {
                if (event.extendedProps.aktivOpdel >= 10 && event.extendedProps.aktivOpdel < 40) {
                    switch (opdel) {
                        case AKTIV_OPDEL.FORBEREDELSER:
                            if (event.extendedProps.forberedelserITid) {
                                return DARK_GREEN;
                            } else if (event.extendedProps.forberedelserITid === false) {
                                return DARK_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        case AKTIV_OPDEL.OPTID:
                            if (event.extendedProps.optidITid) {
                                return DARK_GREEN;
                            } else if (event.extendedProps.optidITid === false) {
                                return DARK_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        case AKTIV_OPDEL.AVVECKLING:
                            if (event.extendedProps.avvecklingITid) {
                                return DARK_GREEN;
                            } else if (event.extendedProps.avvecklingITid === false) {
                                return DARK_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        default:
                            return LIGHT_GREY;
                    }
                } else {
                    switch (opdel) {
                        case AKTIV_OPDEL.FORBEREDELSER:
                            if (event.extendedProps.forberedelserITid) {
                                return LIGHT_GREEN;
                            } else if (event.extendedProps.forberedelserITid === false) {
                                return LIGHT_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        case AKTIV_OPDEL.OPTID:
                            if (event.extendedProps.optidITid) {
                                return LIGHT_GREEN;
                            } else if (event.extendedProps.optidITid === false) {
                                return LIGHT_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        case AKTIV_OPDEL.AVVECKLING:
                            if (event.extendedProps.avvecklingITid) {
                                return LIGHT_GREEN;
                            } else if (event.extendedProps.avvecklingITid === false) {
                                return LIGHT_RED;
                            } else {
                                return LIGHT_GREY;
                            }
                        default:
                            return LIGHT_GREY;
                    }
                }
            },

            minutesUntilXMinuterKvar(event) {
                return (moment(event.extendedProps.xMinuterKvar) - moment(event.start)) / 60000;
            },

            async updateView() {
                // Hämtar salsbemanning
                await this.fetchAndSetSalsbemanningList();

                // Uppdaterar alla resurser/salar med aktuell salsbemanning
                this.setAllResourcesSalsbemanning();

                // Laddar händelser, hämtar operationer och skapar events
                await this.loadHandelserFromSalsbemanningList();
                await this.loadEventsForCurrentView();
            },

            async loadEventsForCurrentView() {
                const params = {
                    bokad_operation_datum: this.currentDate,
                };

                const response = await klinikenApi.get(this.apiUrl, getConfig({ params: params }));
                let operationer = response.data;

                const events = this.createEvents(operationer);

                this.handelser.forEach((handelse) => {
                    const backgroundEvent = {
                        title: handelse.namn,
                        start: handelse.fran,
                        end: handelse.till,
                        display: "background",
                        resourceId: handelse.sal,
                    };
                    events.push(backgroundEvent);
                });

                this.$set(this.calendarOptions, "events", events);
            },

            createEvents(operationer) {
                let events = [];
                operationer.forEach((op) => {
                    const event = {
                        start: op.pagaende_op_forberedelser_start,
                        end: op.pagaende_op_avveckling_slut,
                        resourceId: op.current_sal ?? "0",
                        extendedProps: {
                            anmalanId: op.id,
                            opjournalId: op.operation_id,
                            showPopup: false,
                            opkod: op.current_huvudoperationskod_display,
                            biopkoder: op.current_bioperationskoder_display,
                            huvuddiagnos: op.current_huvuddiagnos_display,
                            bidiagnoser: op.current_bidiagnoser_display,
                            patient: op.patient_display,
                            huvudoperator: op.current_huvudoperator_display,
                            opsal: op.current_sal_display,
                            typoperation: op.current_typoperation_display,
                            sida: op.current_lateralitet_display,
                            forberedelser: op.pagaende_op_forberedelser_minuter,
                            forberedelserITid: op.pagaende_op_forberedelser_i_tid,
                            optidStart: op.pagaende_op_optid_start,
                            optid: op.pagaende_op_optid_minuter,
                            xMinuterKvar: op.pagaende_op_x_minuter_kvar,
                            optidITid: op.pagaende_op_optid_i_tid,
                            avvecklingStart: op.pagaende_op_avveckling_start,
                            avveckling: op.pagaende_op_avveckling_minuter,
                            avvecklingITid: op.pagaende_op_avveckling_i_tid,
                            salstid: op.pagaende_op_salstid_minuter,
                            status: op.status,
                            aktivOpdel: op.pagaende_op_aktiv_opdel,
                            forberedelseKvar: op.pagaende_op_forberedelser_minuter_kvar,
                            optidKvar: op.pagaende_op_optid_minuter_kvar,
                            avvecklingKvar: op.pagaende_op_avveckling_minuter_kvar,
                            salstidKvar: op.pagaende_op_salstid_minuter_kvar,
                            opjournalTider: op.operationsjournal_tider,
                            nivaFranTill: op.current_niva_fran_till_display,
                            preanestesibedomningInformation: op.aktuell_preanestesibedomning?.ordinationer_operation,
                            planerad_optid: op.planerad_optid,
                        },
                    };
                    events.push(event);
                });
                return events;
            },

            eventClick(eventClickInfo) {
                eventClickInfo.jsEvent.preventDefault();

                const event = eventClickInfo.event;
                event.setExtendedProp("showPopup", !event.extendedProps.showPopup);
            },

            openPersonalOchTider(salsbemanning) {
                this.salsbemanning = salsbemanning;
                this.showPersonalOchTider = true;
            },
            closePersonalOchTider() {
                this.showPersonalOchTider = false;
                this.salsbemanning = null;
            },
            personalOchTiderSubmitted(newSalsbemanning) {
                // Uppdaterar eller lägger till salsbemanningen
                const index = this.salsbemanningList.findIndex((item) => item.id === newSalsbemanning.id);
                if (index !== -1) {
                    this.salsbemanningList[index] = newSalsbemanning;
                } else {
                    this.salsbemanningList.push(newSalsbemanning);
                }

                // Uppdaterar alla salar med aktuell salsbemanning
                this.setAllResourcesSalsbemanning();

                // Laddar händelser, byter ut befintliga bakgrundsevents mot nya listan av händelser och uppdaterar events
                this.loadHandelserFromSalsbemanningList();
                const events = [...this.calendarOptions.events];
                const filteredEvents = [
                    ...events.filter((e) => e.display !== "background"),
                    ...this.handelser.map((handelse) => ({
                        title: handelse.namn,
                        start: handelse.fran,
                        end: handelse.till,
                        display: "background",
                        resourceId: handelse.sal,
                    })),
                ];
                this.$set(this.calendarOptions, "events", filteredEvents);

                this.closePersonalOchTider();
            },

            setReloadTimeout() {
                if (this.reloadTimeout) {
                    clearTimeout(this.reloadTimeout);
                }

                this.reloadTimeout = setTimeout(async () => {
                    await this.updateView();
                    this.reloadTimeout = null;
                    this.setReloadTimeout();
                }, this.reloadIdleTime);
            },

            async fetchAndSetSalsbemanningList() {
                const salsbemanningResponse = await klinikenApi.get(
                    `/operation/salsbemanning/`,
                    getConfig({
                        params: {
                            datum: this.currentDate,
                        },
                    })
                );
                this.salsbemanningList = salsbemanningResponse?.data?.results ?? [];
            },

            updateSalsbemanningOnResource(resource) {
                resource.salsbemanning = this.salsbemanningList.find((item) => item.sal == resource.id) ?? {
                    id: null,
                    sal: resource.id,
                    sal_display: {
                        id: resource.id,
                        namn: resource.namn,
                        ordning: resource.ordning,
                        org_units: resource.org_units,
                        orgunits_display: resource.orgunits_display,
                    },
                    datum: this.currentDate,
                    personal: [],
                    handelser: [],
                    kommentar: null,
                };
                return resource;
            },

            setAllResourcesSalsbemanning() {
                const resources = this.calendarOptions.resources.map((resource) => {
                    return this.updateSalsbemanningOnResource(resource);
                });
                this.$set(this.calendarOptions, "resources", resources);
            },

            async loadHandelserFromSalsbemanningList() {
                const handelser = [];
                this.salsbemanningList.forEach((item) => {
                    item.handelser.forEach((handelse) => {
                        handelser.push({ ...handelse, sal: item.sal });
                    });
                });
                this.handelser = handelser;
            },
        },

        async mounted() {
            if (!this.can({ action: "read", subject: "pagaendeoperation" })) {
                await this.$store.dispatch("tabs/closeTab", this.tabId);
                return;
            }

            this.canReadOperationsjournal = this.can({ action: "read", subject: "operationsjournal" });

            // Initierar kalenderfunktionerna
            this.calendarApi = await this.$refs.fullCalendar.getApi();

            // Hämtar salsbemanning
            await this.fetchAndSetSalsbemanningList();

            // Initierar resurser/salar
            const salarResponse = await klinikenApi.get("/operation/salar/", getConfig());
            let resources = salarResponse.data?.results ?? [];
            resources = resources.map((resource) => {
                resource = this.updateSalsbemanningOnResource(resource);
                resource.title = resource.namn;
                return resource;
            });
            this.$set(this.calendarOptions, "resources", resources);

            // Laddar händelser, hämtar operationer och skapar events
            this.loadHandelserFromSalsbemanningList();
            await this.loadEventsForCurrentView();
            this.setReloadTimeout();

            if (this.pathname === "/pagaendeop") {
                this.$store.commit("triggers/addListener", {
                    id: this.tabName,
                    eventName: "onDayChange",
                    listener: () => {
                        this.$store.dispatch("userData/logout");
                    },
                });
            }

            emitter.on("pagaende-op-tider-updated", async () => await this.updateView());
            emitter.on(`adjust-tab-${this.$options.tabName}`, async () => {
                setTimeout(() => {
                    this.calendarApi.updateSize();
                }, 10);
            });
        },

        beforeDestroy() {
            emitter.off("pagaende-op-tider-updated");
            emitter.off(`adjust-tab-${this.$options.tabName}`);

            if (this.pathname === "/pagaendeop") {
                this.$store.commit("triggers/removeListener", {
                    eventName: "onDayChange",
                    id: this.tabName,
                });
            }

            if (this.reloadTimeout) {
                clearTimeout(this.reloadTimeout);
            }
        },
    };
</script>
<style lang="scss">
    @import "@/style/variables";

    $gap: 24px;

    .pagaende-operation {
        $event-text-color: #55676d;
        $stripes-color: #354052;
        $toolbar-bg-color: #ebf6f8;
        $toolbar-btn-color: #38a7cf;
        $toolbar-text-color: #3092b5;
        $toolbar-arrow-color: #fafbfd;

        .header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 24px;

            .header {
                h1 {
                    margin-bottom: 14px;
                }
            }

            .header-section-left {
                display: flex;
                flex-wrap: wrap;
                flex-direction: row-reverse;
                gap: 20px;

                .legend {
                    height: 68px;
                    width: 30rem;
                    background-color: #fafbfd;
                    border: solid 1px #e4e8f0;
                    border-radius: 4px;
                    display: flex;
                    flex-direction: column;
                    flex-wrap: wrap;
                    padding: 9px;
                    row-gap: 10px;
                    column-gap: 14px;

                    .legend-entry {
                        display: flex;
                        align-items: center;
                        gap: 6px;

                        .color-box {
                            height: 18px;
                            width: 18px;
                            border-radius: 4px;

                            &.in-time {
                                background-color: #34ac8d;
                            }

                            &.time-exceeded {
                                background-color: #e96669;
                            }

                            &.x-min-left {
                                background-image: repeating-linear-gradient(
                                    145deg,
                                    #dee2e3,
                                    #dee2e3 4.5px,
                                    $stripes-color 4.5px,
                                    $stripes-color 6px
                                );
                            }
                        }

                        .legend-text {
                            line-height: 16px;
                            font-size: 14px;
                        }
                    }
                }

                .toolbar {
                    background-color: $toolbar-bg-color;
                    min-width: 166px;
                    height: 68px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0 $gap;
                    gap: $gap;

                    .datepicker {
                        margin: 0;
                        height: 40px;

                        .mx-datepicker {
                            width: 140px;
                            input {
                                padding-block: 0;
                                font-size: 14px;
                                height: 40px;
                            }
                        }
                    }

                    div.show-person-id {
                        display: flex;
                        align-items: center;
                        gap: $gap;

                        .labeled-checkbox {
                            display: flex;
                            align-items: center;
                            gap: 7px;

                            label {
                                font-size: 14px;
                            }

                            .formulate-input {
                                margin-top: 17px;
                            }

                            .checkbox-label {
                                margin: 0;
                            }
                        }
                    }

                    .today-button {
                        background: none;
                        border: none;
                        font-size: 14px;
                        color: $toolbar-text-color;
                    }

                    .active-tab {
                        font-weight: bold;
                        text-decoration: underline;
                        text-decoration-thickness: 3px;
                        text-underline-offset: 12px;
                    }

                    .arrow-buttons {
                        display: flex;
                        gap: 12px;

                        .arrow-button {
                            background: $toolbar-btn-color;
                            border: none;
                            border-radius: 4px;
                            width: 34px;
                            height: 34px;
                            color: $toolbar-arrow-color;
                        }
                    }
                }
            }
        }

        .resource-header {
            position: relative;
            top: -0.25rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-family: "Roboto Medium";
            font-size: 0.875rem;
        }

        .box {
            background-color: $color-background-tool-menu;
            border-radius: 0.355rem;
            padding: 0.313rem;
        }

        .salsbemanning {
            display: flex;
            flex-wrap: wrap;
            gap: 0.438rem;
            min-height: 2rem;

            .personal {
                display: flex;
                gap: 0.25rem;

                &.kirurg {
                    font-family: "Roboto Medium";
                }

                .personal-icon {
                    height: 1.325rem;
                    width: 1.325rem;
                }

                .personal-name {
                    font-size: 0.625rem;
                    line-height: 0.75rem;
                }

                .personal-time {
                    font-size: 0.5rem;
                    line-height: 0.625rem;
                }
            }
        }

        > * {
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .salsbemanning-kommentar {
            margin-top: 0.438rem;
            width: fit-content;
            padding: 0.375rem 0.5rem;
            font-size: 0.625rem;
            line-height: 0.875rem;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre;
        }

        .fc-bg-event {
            opacity: 1;

            .background-event {
                display: flex;
                justify-content: center;
                background-color: #f6f8fb;
                border: 1px #d3d8da solid;
                border-radius: 0.375rem;
                margin: 0.375rem 0.188rem;
                height: 100%;
                color: $color-text;
                font-size: 0.625rem;
                line-height: 0.875rem;
            }
        }

        .fc-datagrid-cell {
            &.fc-resource {
                padding-bottom: 1rem;
            }
        }
        .fc-timeline-lane {
            &.fc-resource {
                padding-bottom: 1rem;
            }
        }

        .fc-timeline-events {
            min-height: 46px;

            .fc-timeline-event-harness {
                margin-top: 21px;
                height: 52px;

                a.fc-event {
                    padding: 0;
                    height: 100%;

                    .fc-event-main {
                        .event-info {
                            display: flex;
                            gap: 6px;
                            color: $event-text-color;
                            font-size: 14px;
                            white-space: nowrap;
                            text-overflow: ellipsis;
                            overflow: hidden;
                            margin-left: 3px;

                            .person-id {
                                margin-left: auto; /* Flyttar person-ID till längst ut av raden */
                            }
                        }

                        .op-body-wrapper {
                            height: 30px;
                            display: flex;
                            border-radius: 4px;
                            overflow: hidden;

                            // (Tips till senare: för att inte få vita kanter på operationer utan
                            // förberedelser/avveckling - kolla om opdelarna har syskon)
                            .forberedelser {
                                border-right: 1px solid white;
                            }

                            .optid {
                                border-left: 1px solid white;
                                border-right: 1px solid white;
                            }

                            .avveckling {
                                border-left: 1px solid white;
                            }

                            .x-minuter-kvar {
                                position: absolute;
                                min-height: 30px;
                                background-image: repeating-linear-gradient(
                                    145deg,
                                    transparent,
                                    transparent 4.5px,
                                    $event-text-color 4.5px,
                                    $event-text-color 6px
                                );
                            }
                        }
                    }
                }
            }
        }
    }
</style>
